import { useEffect, useRef } from 'react'
import type { Transcript, TranscriptSpeaker, SegmentWithWords } from '../types'
import { usePlayerStore } from '../store/usePlayerStore'
import TranscriptSegment from './TranscriptSegment'

interface TranscriptListProps {
  transcript: Transcript
  speakers?: TranscriptSpeaker[]
  speakerNames?: Record<string, string>
  segmentsWithWords?: SegmentWithWords[] | null
  onEdit?: (index: number, field: 'text' | 'speaker', value: string) => void
  onSplit?: (index: number, charPosition: number) => void
  onWordCorrect?: (segmentIndex: number, wordIndex: number, correctedTo: string) => void
}

export default function TranscriptList({
  transcript,
  speakers,
  speakerNames,
  segmentsWithWords,
  onEdit,
  onSplit,
  onWordCorrect,
}: TranscriptListProps) {
  const { currentTime, setActiveSegment, activeSegmentIndex } = usePlayerStore()
  const listRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLDivElement>(null)

  // Find active segment based on current time
  useEffect(() => {
    const activeIndex = transcript.segments.findIndex(
      (seg, i) => {
        const nextSeg = transcript.segments[i + 1]
        return currentTime >= seg.start && (!nextSeg || currentTime < nextSeg.start)
      }
    )
    setActiveSegment(activeIndex >= 0 ? activeIndex : null)
  }, [currentTime, transcript.segments, setActiveSegment])

  // Auto-scroll to active segment
  useEffect(() => {
    if (activeRef.current && listRef.current) {
      const container = listRef.current
      const element = activeRef.current
      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()

      if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [activeSegmentIndex])

  // Get words for a segment by index
  const getWordsForSegment = (index: number) => {
    if (!segmentsWithWords) return null
    const segmentWithWords = segmentsWithWords.find(s => s.index === index)
    return segmentWithWords?.words || null
  }

  // Group segments by sections if available
  const renderWithSections = () => {
    if (!transcript.sections || transcript.sections.length === 0) {
      return transcript.segments.map((segment, index) => (
        <div key={index} ref={index === activeSegmentIndex ? activeRef : null}>
          <TranscriptSegment
            segment={segment}
            index={index}
            isActive={index === activeSegmentIndex}
            speakers={speakers || transcript.speakers || undefined}
            speakerNames={speakerNames}
            words={getWordsForSegment(index)}
            onEdit={onEdit}
            onSplit={onSplit}
            onWordCorrect={onWordCorrect}
          />
        </div>
      ))
    }

    const elements: JSX.Element[] = []

    transcript.sections.forEach((section, sectionIndex) => {
      // Section header
      elements.push(
        <div
          key={`section-${sectionIndex}`}
          className="py-4 border-b border-gray-200 mb-4 mt-6 first:mt-0"
        >
          <h3 className="text-lg font-semibold text-gray-700">{section.title}</h3>
          <span className="text-xs text-gray-400">{section.type}</span>
        </div>
      )

      // Segments in this section
      for (let i = section.start_segment; i <= section.end_segment && i < transcript.segments.length; i++) {
        elements.push(
          <div key={`segment-${i}`} ref={i === activeSegmentIndex ? activeRef : null}>
            <TranscriptSegment
              segment={transcript.segments[i]}
              index={i}
              isActive={i === activeSegmentIndex}
              speakers={speakers || transcript.speakers || undefined}
              speakerNames={speakerNames}
              words={getWordsForSegment(i)}
              onEdit={onEdit}
              onSplit={onSplit}
              onWordCorrect={onWordCorrect}
            />
          </div>
        )
      }
    })

    return elements
  }

  return (
    <div ref={listRef} className="space-y-1 p-4">
      {renderWithSections()}
    </div>
  )
}
